// src/pages/Expenses.js
import React, { useState, useEffect } from 'react';
import { T, Card, Btn, SectionTitle, Modal, Spinner, Empty } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { listExpenses, createExpense, deleteExpense } from '../services/api';

const INP = { background:T.surface, border:`1px solid ${T.border}`, borderRadius:'8px', padding:'10px 14px', color:T.text, fontSize:'14px', width:'100%', outline:'none', boxSizing:'border-box', marginBottom:'12px' };
const LBL = { fontSize:'11px', color:T.muted, marginBottom:'5px', display:'block', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' };

const CATEGORIES = ['Paper & Printing Supplies','Ink & Toner','Equipment Maintenance','Utilities','Transport','Office Supplies','Staff Welfare','Other'];

export default function Expenses() {
  const { can } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    setLoading(true);
    listExpenses()
      .then(r => { setExpenses(r.data.data); setTotal(r.data.total_expenses); })
      .catch(() => setMsg({ type:'error', text:'Failed to load expenses.' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    try {
      await deleteExpense(id);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.response?.data?.message || 'Failed to delete.' });
    }
  };

  const fmt = (n) => `KES ${Number(n || 0).toLocaleString()}`;

  return (
    <div>
      <SectionTitle sub="Business expenses recorded by Finance. Each expense deducts from the Business Fund.">Expenses</SectionTitle>

      {msg && <div style={{ color:T.danger, fontSize:'13px', marginBottom:'14px' }}>{msg.text}</div>}

      <div style={{ display:'flex', gap:'14px', marginBottom:'20px', flexWrap:'wrap' }}>
        <Card style={{ flex:1, minWidth:'180px' }}>
          <div style={{ fontSize:'11px', color:T.muted, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>Total Expenses (all time)</div>
          <div style={{ fontSize:'24px', fontWeight:800, color:T.danger }}>{fmt(total)}</div>
          <div style={{ fontSize:'12px', color:T.muted, marginTop:'4px' }}>Deducted from Business Fund</div>
        </Card>

        {can('record_expenses') && (
          <div style={{ display:'flex', alignItems:'flex-start', paddingTop:'10px' }}>
            <Btn onClick={() => setShowAdd(true)}>+ Record expense</Btn>
          </div>
        )}
      </div>

      {loading ? <Spinner /> : expenses.length === 0 ? (
        <Empty msg="No expenses recorded yet." />
      ) : (
        <Card style={{ padding:0, overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Date</th>
                <th style={TH}>Category</th>
                <th style={TH}>Description</th>
                <th style={TH}>Linked Order</th>
                <th style={TH}>Recorded By</th>
                <th style={TH}>Amount</th>
                {can('record_expenses') && <th style={TH}></th>}
              </tr>
            </thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id}>
                  <td style={TD}>{new Date(e.created_at).toLocaleDateString('en-KE')}</td>
                  <td style={TD}><span style={{ fontSize:'11px', background:`${T.border}`, borderRadius:'5px', padding:'3px 8px' }}>{e.category}</span></td>
                  <td style={TD}>{e.description || '—'}</td>
                  <td style={{ ...TD, fontFamily:'monospace', fontSize:'12px', color:T.gold }}>{e.order_number || '—'}</td>
                  <td style={TD}>{e.recorded_by_name}</td>
                  <td style={{ ...TD, fontWeight:700, color:T.danger }}>−{fmt(e.amount)}</td>
                  {can('record_expenses') && (
                    <td style={TD}>
                      <Btn size="sm" variant="danger" onClick={() => handleDelete(e.id)}>Delete</Btn>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showAdd && (
        <AddExpenseModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function AddExpenseModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ amount:'', category: CATEGORIES[0], description:'', order_id:'' });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setMsg(null);
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) return setMsg('Enter a valid amount greater than 0.');
    if (!form.category) return setMsg('Select a category.');

    setLoading(true);
    try {
      await createExpense({
        amount,
        category: form.category,
        description: form.description || null,
        order_id: form.order_id ? Number(form.order_id) : null,
      });
      onSaved();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to record expense.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Record expense" onClose={onClose}>
      <label style={LBL}>Amount (KES)</label>
      <input style={INP} type="number" min="1" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="e.g. 3500" />

      <label style={LBL}>Category</label>
      <select style={INP} value={form.category} onChange={e => set('category', e.target.value)}>
        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>

      <label style={LBL}>Description (optional)</label>
      <input style={INP} value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Purchased 5 reams of A3 paper" />

      <label style={LBL}>Linked Order ID (optional)</label>
      <input style={INP} type="number" value={form.order_id} onChange={e => set('order_id', e.target.value)} placeholder="Leave blank if not linked to a specific order" />

      {msg && <div style={{ color:T.danger, fontSize:'12px', marginBottom:'12px' }}>{msg}</div>}

      <Btn onClick={submit} disabled={loading}>{loading ? 'Recording...' : 'Record expense'}</Btn>
    </Modal>
  );
}

const TH = { textAlign:'left', padding:'10px 14px', fontSize:'11px', color:T.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:`1px solid ${T.border}` };
const TD = { padding:'11px 14px', fontSize:'13px', borderBottom:`1px solid ${T.surface}` };
